// Laeuft der Stand, den der Server ausliefert? Wenn nicht: einmal neu laden.
//
// Hintergrund (U800F, 07.10. und 20.10.2026): Nach Stromlos oder Neustart
// startet die App oft, bevor das Netz steht. Der Loader (tizenStartapp)
// erreicht build.json dann nicht und nimmt die zuletzt gemerkte Build-Id -
// die Bundles kommen unter dieser Id aus dem Cache, es laeuft der alte Stand,
// bis jemand "App neu laden" drueckt. Ab Loader 2.7.6 wartet der Loader kurz
// auf das Netz; diese Pruefung holt Geraete mit aelterem Loader ein und faengt
// den Fall ab, dass auch die Wartezeit nicht gereicht hat.
//
// Ablauf, ohne den Start aufzuhalten (laeuft neben der App):
//   1. auf das Netz warten (webapis.network.isConnectedToGateway), hoechstens
//      NET_WAIT_MS,
//   2. build.json vom Server lesen (Zeitstempel in der URL, nie aus dem Cache),
//   3. weicht die Id vom eigenen Build ab: einmal neu laden.
//
// Nie haengenbleiben, nie in eine Schleife:
//   - Neu geladen wird nur in den ersten RELOAD_WINDOW_MS nach dem Start.
//     Spaeter schaut der Gast vielleicht schon fern; dann nur ein Log, der
//     naechste Start holt den Stand.
//   - Pro Server-Id hoechstens EIN Neuladen (Merker im localStorage). Bringt
//     es den Stand nicht, bleibt es dabei.
//   - Nicht, solange ein Cast-Standby-Neustart nachgeholt wird (der Merker
//     cast_reboot_pending_at, siehe store/modules/cast.js) - der Reboot
//     macht ohnehin einen frischen Start.
//   - Hat der Loader 2.7.6+ build.json beim Start selbst gelesen
//     (window.__loaderBuildSource === 'server'), ist nichts zu pruefen.
//
// Mischstaende (WGT alt/neu x tv-app alt/neu) bleiben lauffaehig:
//   - Nur Loader, die selbst mit build.json arbeiten (ab 2.7.4, erkennbar an
//     BUILD_URL im Dokument), werden geprueft. Die alten Loader 2.0/2.0.1
//     haengen einen festen bzw. zufaelligen Cache-Buster an - ein Neuladen
//     aendert dort nichts, also wird es gar nicht erst versucht. LG, Chrome
//     und Orsay haben keinen solchen Loader.
//   - Gefragt wird genau die build.json, die auch der Loader liest (BUILD_URL
//     des Loaders), nicht process.env.HOST - ein lokal gebauter Stand mit
//     Staging-HOST wuerde sonst den falschen Server vergleichen.
//   - Fehlt build.json (alte tv-app auf dem Server, Update-Uebergang) oder
//     antwortet der Server nicht mit 200: keine Aussage, kein Neuladen.

/* global BUILD_URL */

import AppBuild from './AppBuild';

const NET_WAIT_MS = 30000;
const NET_POLL_MS = 1000;
const FETCH_TIMEOUT_MS = 5000;
const FETCH_TRIES = 3;
const FETCH_RETRY_MS = 3000;
const RELOAD_WINDOW_MS = 90000;
const RELOADED_FOR_KEY = 'build_check_reloaded_for';
const CAST_REBOOT_PENDING_KEY = 'cast_reboot_pending_at';

function log(msg) {
  console.log('[BuildCheck] ' + msg);
}

function readKey(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeKey(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* ohne localStorage kein Schleifenschutz - dann lieber gar nicht neu laden */
  }
}

// true / false, oder null, wenn die Abfrage nicht verfuegbar ist.
function gatewayConnected() {
  try {
    const net = window.webapis && window.webapis.network;
    if (!net || typeof net.isConnectedToGateway !== 'function') return null;
    return !!net.isConnectedToGateway();
  } catch (e) {
    return null;
  }
}

function waitForNetwork() {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const check = () => {
      const c = gatewayConnected();
      if (c !== false) {
        if (Date.now() - t0 > NET_POLL_MS) log('Netz da nach ' + (Date.now() - t0) + ' ms');
        resolve(true);
      } else if (Date.now() - t0 >= NET_WAIT_MS) {
        log('kein Gateway nach ' + NET_WAIT_MS + ' ms - Pruefung entfaellt');
        resolve(false);
      } else {
        setTimeout(check, NET_POLL_MS);
      }
    };
    check();
  });
}

// build.json-Adresse des Loaders, oder null bei Loadern ohne build.json.
// Top-Level-const eines klassischen Skripts ist fuer alle Skripte desselben
// Dokuments sichtbar - die App laeuft im Dokument des Loaders.
function loaderBuildUrl() {
  try {
    return typeof BUILD_URL !== 'undefined' && BUILD_URL ? String(BUILD_URL) : null;
  } catch (e) {
    return null;
  }
}

// Build-Id vom Server; false = Server antwortet, aber ohne brauchbare
// build.json (nicht wiederholen); null = nicht erreichbar (wiederholen).
function fetchServerBuild(url) {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url + '?t=' + Date.now(), true);
      xhr.timeout = FETCH_TIMEOUT_MS;
      xhr.onload = () => {
        if (xhr.status && xhr.status !== 200) {
          resolve(false);
          return;
        }
        try {
          resolve(String((JSON.parse(xhr.responseText) || {}).build || '').trim() || false);
        } catch (e) {
          resolve(false);
        }
      };
      xhr.onerror = () => resolve(null);
      xhr.ontimeout = () => resolve(null);
      xhr.send();
    } catch (e) {
      resolve(null);
    }
  });
}

function fetchWithRetry(url, tries) {
  return fetchServerBuild(url).then((build) => {
    if (build !== null || tries <= 1) return build || null;
    return new Promise((r) => setTimeout(r, FETCH_RETRY_MS)).then(() => fetchWithRetry(url, tries - 1));
  });
}

// reload: Funktion, die die App sauber neu laedt (socket/runAppReload).
export function startBuildCheck(reload) {
  const own = AppBuild.build;
  const url = loaderBuildUrl();
  if (!own) return; // Dev-Build ohne Build-Id
  if (!url) return; // Loader ohne build.json (2.0.x), LG, Chrome
  if (window.__loaderBuildSource === 'server') return;

  waitForNetwork()
    .then((ok) => (ok ? fetchWithRetry(url, FETCH_TRIES) : null))
    .then((server) => {
      if (!server) {
        log('build.json nicht lesbar - bleibe bei ' + own);
        return;
      }
      if (server === own) {
        log('aktueller Stand laeuft: ' + own);
        return;
      }
      const age = Date.now() - AppBuild.startedAt;
      if (readKey(RELOADED_FOR_KEY) === server) {
        log('Server ' + server + ', hier ' + own + ' - wurde schon einmal neu geladen, kein zweiter Versuch');
        return;
      }
      if (age > RELOAD_WINDOW_MS) {
        log('Server ' + server + ', hier ' + own + ' - App laeuft schon ' + Math.round(age / 1000) + ' s, kein Neuladen');
        return;
      }
      if (readKey(CAST_REBOOT_PENDING_KEY)) {
        log('Server ' + server + ', hier ' + own + ' - Cast-Neustart steht an, kein Neuladen');
        return;
      }
      writeKey(RELOADED_FOR_KEY, server);
      if (readKey(RELOADED_FOR_KEY) !== server) {
        log('Merker nicht schreibbar - kein Neuladen');
        return;
      }
      log('Server ' + server + ', hier ' + own + ' (Loader: ' + (AppBuild.loaderBuild || '-') + ') - lade einmal neu');
      reload();
    });
}

export default { startBuildCheck };



// WEBPACK FOOTER //
// ./src/helpers/BuildCheck.js